// ABOUTME: Global context for symbol source selection (portfolio or watchlist)
// ABOUTME: Provides symbols from selected source to all analysis pages

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useWatchlists } from '../hooks/useWatchlists';
import { safeGetJson, safeRemove, safeSetJson } from '../lib/storage';
import { usePortfolios } from './PortfolioContext';

export type SourceType = 'portfolio' | 'watchlist';

export interface SymbolSource {
  type: SourceType;
  id: string;
  name: string;
  symbols: string[];
  symbolCount: number;
}

interface SymbolSourceContextValue {
  source: SymbolSource | null;
  setSource: (source: SymbolSource | null) => void;
  /** Resolve and activate a source by id (e.g. from a ?portfolio= / ?watchlist= deep-link). */
  selectSource: (type: SourceType, id: string) => void;
  symbols: string[];
  isLoading: boolean;
}

const SymbolSourceContext = createContext<SymbolSourceContextValue | null>(null);

const STORAGE_KEY = 'alpha-finder-symbol-source';

interface StoredSource {
  type: SourceType;
  id: string;
}

export function SymbolSourceProvider({ children }: { children: ReactNode }) {
  const {
    portfolios,
    activePortfolio,
    isLoading: portfolioLoading,
    setActivePortfolioId,
  } = usePortfolios();
  const [source, setSourceState] = useState<SymbolSource | null>(null);
  // Tracks whether a source was explicitly selected (e.g. via a deep-link) so the
  // init effect can skip its default-portfolio assignment even on the same render.
  const explicitlySetRef = useRef(false);
  const [storedSource, setStoredSource] = useState<StoredSource | null>(() =>
    safeGetJson<StoredSource>(STORAGE_KEY)
  );

  // Fetch watchlists. Stabilize the empty default: a bare `?? []` (or a destructuring
  // default) allocates a fresh array every render, and `watchlists` is a dependency of
  // the source-init effect below — a new reference each render spun that effect (which
  // setStates the source) into a render loop until React's update-depth guard aborted
  // it, leaving browser scores stuck "loading"/stale.
  const { data: watchlistsData, isLoading: watchlistsLoading } = useWatchlists();
  const watchlists = useMemo(() => watchlistsData ?? [], [watchlistsData]);

  // Initialize source from stored preference or default to active portfolio
  useEffect(() => {
    if (explicitlySetRef.current) return; // A source was explicitly selected

    // Try to restore from stored preference
    if (storedSource) {
      if (storedSource.type === 'portfolio' && activePortfolio) {
        if (storedSource.id === activePortfolio.id) {
          const symbols = (activePortfolio.positions || []).map((p) => p.symbol);
          setSourceState({
            type: 'portfolio',
            id: activePortfolio.id,
            name: activePortfolio.name,
            symbols,
            symbolCount: symbols.length,
          });
          return;
        }
      } else if (storedSource.type === 'watchlist' && watchlists.length > 0) {
        const watchlist = watchlists.find((w) => w.id === storedSource.id);
        if (watchlist) {
          const symbols = watchlist.items.map((i) => i.symbol);
          setSourceState({
            type: 'watchlist',
            id: watchlist.id,
            name: watchlist.name,
            symbols,
            symbolCount: symbols.length,
          });
          return;
        }
      }
    }

    // Default to active portfolio
    if (activePortfolio) {
      const symbols = (activePortfolio.positions || []).map((p) => p.symbol);
      setSourceState({
        type: 'portfolio',
        id: activePortfolio.id,
        name: activePortfolio.name,
        symbols,
        symbolCount: symbols.length,
      });
    }
  }, [activePortfolio, watchlists, storedSource]);

  // Update source when activePortfolio changes and we're tracking a portfolio
  useEffect(() => {
    if (source?.type === 'portfolio' && activePortfolio && source.id === activePortfolio.id) {
      const symbols = (activePortfolio.positions || []).map((p) => p.symbol);
      setSourceState({
        type: 'portfolio',
        id: activePortfolio.id,
        name: activePortfolio.name,
        symbols,
        symbolCount: symbols.length,
      });
    }
  }, [activePortfolio, source?.type, source?.id]);

  const setSource = useCallback((newSource: SymbolSource | null) => {
    explicitlySetRef.current = newSource !== null;
    setSourceState(newSource);
    if (newSource) {
      const toStore: StoredSource = { type: newSource.type, id: newSource.id };
      safeSetJson(STORAGE_KEY, toStore);
      setStoredSource(toStore);
    } else {
      safeRemove(STORAGE_KEY);
      setStoredSource(null);
    }
  }, []);

  // Resolve a source by id from the loaded portfolio/watchlist lists. For a
  // portfolio we also activate it so the enriched fetch fills in fresh symbols.
  const selectSource = useCallback(
    (type: SourceType, id: string) => {
      if (type === 'watchlist') {
        const watchlist = watchlists.find((w) => w.id === id);
        if (!watchlist) return;
        const symbols = watchlist.items.map((i) => i.symbol);
        setSource({ type, id, name: watchlist.name, symbols, symbolCount: symbols.length });
        return;
      }

      setActivePortfolioId(id);
      const portfolio = portfolios.find((p) => p.id === id);
      const symbols =
        activePortfolio?.id === id ? activePortfolio.positions.map((p) => p.symbol) : [];
      setSource({ type, id, name: portfolio?.name ?? id, symbols, symbolCount: symbols.length });
    },
    [watchlists, portfolios, activePortfolio, setActivePortfolioId, setSource]
  );

  const symbols = useMemo(() => (source ? source.symbols : []), [source]);
  const isLoading = portfolioLoading || watchlistsLoading;

  const value = useMemo(
    () => ({ source, setSource, selectSource, symbols, isLoading }),
    [source, setSource, selectSource, symbols, isLoading]
  );

  return <SymbolSourceContext.Provider value={value}>{children}</SymbolSourceContext.Provider>;
}

export function useSymbolSource() {
  const context = useContext(SymbolSourceContext);
  if (!context) {
    throw new Error('useSymbolSource must be used within a SymbolSourceProvider');
  }
  return context;
}
